import { useCallback } from 'react'
import { IDEOLOGY_AXIS_COUNT } from '@/constants/ideology'
import { createNeutralVector } from '@/lib/ideology'
import { cn } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import { IdeologySlider } from './IdeologySlider'
import { IdeologyDisplay } from './IdeologyDisplay'
import type { IdeologyVector } from '@/types'

interface IdeologyEditorProps {
  value: IdeologyVector
  onChange: (value: IdeologyVector) => void
  partyColor?: string
  disabled?: boolean
  showPreview?: boolean
  className?: string
}

export function IdeologyEditor({
  value,
  onChange,
  partyColor,
  disabled = false,
  showPreview = true,
  className,
}: IdeologyEditorProps) {
  const handleAxisChange = useCallback(
    (axisIndex: number, axisValue: number) => {
      const newVector = [...value] as IdeologyVector
      newVector[axisIndex] = axisValue
      onChange(newVector)
    },
    [value, onChange]
  )

  const handleReset = useCallback(() => {
    onChange(createNeutralVector())
  }, [onChange])

  return (
    <div className={cn('space-y-6', className)}>
      <div className={cn(
        'grid gap-8',
        showPreview && 'lg:grid-cols-[1fr,300px]'
      )}>
        {/* Sliders section */}
        <div className="space-y-6">
          {Array.from({ length: IDEOLOGY_AXIS_COUNT }, (_, index) => (
            <IdeologySlider
              key={index}
              axisIndex={index}
              value={value[index]}
              onChange={(v) => handleAxisChange(index, v)}
              partyColor={partyColor}
              disabled={disabled}
            />
          ))}

          <div className="pt-2">
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={handleReset}
              disabled={disabled}
            >
              Reset to Center
            </Button>
          </div>
        </div>

        {/* Preview section - only on larger screens */}
        {showPreview && (
          <div className="hidden lg:block">
            <div className="sticky top-4 space-y-3">
              <h4 className="text-sm font-medium text-muted-foreground">
                Ideology Preview
              </h4>
              <div className="rounded-lg border bg-card p-4">
                <IdeologyDisplay ideology={value} compact />
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Mobile preview - collapsible or always visible at bottom */}
      {showPreview && (
        <div className="lg:hidden">
          <details className="group">
            <summary className="cursor-pointer text-sm font-medium text-muted-foreground hover:text-foreground">
              <span className="group-open:hidden">Show Ideology Preview</span>
              <span className="hidden group-open:inline">Hide Ideology Preview</span>
            </summary>
            <div className="mt-3 rounded-lg border bg-card p-4">
              <IdeologyDisplay ideology={value} compact />
            </div>
          </details>
        </div>
      )}
    </div>
  )
}
